/**
 * Tabs — Organizes content into mutually exclusive panels activated by tab triggers.
 *
 * Tabs Component Guidelines
 * - Use `variant="default"` for primary content tabs (filled background indicator).
 *   Use `variant="outline"` for secondary or nested tabs (underline indicator).
 * - Use `size="base"` for page-level tabs; `size="sm"` for tabs inside panels,
 *   popovers, or sidebars.
 * - Use `orientation="vertical"` for side navigation patterns where tabs stack
 *   vertically with content beside them.
 * - Use `startEnhancer` / `endEnhancer` on `TabsTrigger` for icons beside tab labels.
 * - The animated indicator transitions smoothly between tabs — no additional animation
 *   setup is needed.
 * @see https://base-ui.com/react/components/tabs Base UI Tabs documentation
 * @see https://ui.shadcn.com/docs/components/tabs shadcn/ui Tabs documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

interface TabsProps<ClassName extends string = string>
  extends Omit<TabsPrimitive.Root.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const Tabs = <ClassName extends string>({
  className,
  UNSAFE_className,
  orientation,
  ...props
}: TabsProps<ClassName>) => {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      orientation={orientation}
      className={cn(
        "flex",
        orientation === "vertical" ? "flex-row" : "flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
};

interface AnimatedIndicatorProps {
  activeTabPosition: { left: number; top: number } | null;
  activeTabSize: { width: number; height: number } | null;
  orientation: "horizontal" | "vertical";
  variant: "default" | "outline";
  size: "base" | "sm";
  className?: string;
}

const AnimatedIndicator: React.FC<AnimatedIndicatorProps> = ({
  activeTabPosition,
  activeTabSize,
  orientation,
  variant,
  size,
  className,
}) => {
  const isHorizontal = orientation === "horizontal";

  const hasPosition = Boolean(activeTabPosition) && Boolean(activeTabSize);

  const isOutline = variant === "outline";

  const inset = size === "base" ? 8 : 4;

  const getIndicatorStyle = ():
    | React.CSSProperties
    | Record<string, number | string> => {
    if (isOutline) {
      if (isHorizontal) {
        return {
          bottom: size === "base" ? "-2px" : "0px",
          left: (activeTabPosition?.left ?? 0) + inset,
          width: (activeTabSize?.width ?? 0) - inset * 2,
        };
      }
      return {
        left: "0px",
        width: "2px",
        top: (activeTabPosition?.top ?? 0) + inset,
        height: (activeTabSize?.height ?? 0) - inset * 2,
      };
    }
    return {
      left: activeTabPosition?.left ?? 0,
      top: activeTabPosition?.top ?? 0,
      width: activeTabSize?.width ?? 0,
      height: activeTabSize?.height ?? 0,
    };
  };

  // NOTE (Ezra, 2026-02-09): Don't render until Base UI has measured the active
  // tab's position. This prevents the indicator from animating in from 0,0 on
  // mount (e.g. when tabs render inside a popover or lazy container).
  if (!hasPosition) {
    return null;
  }

  return (
    <span
      className={cn(
        "pointer-events-none absolute transition-all duration-300 ease-in-out",
        !isOutline && "rounded-lg border-0.5 border-border z-0",
        !isOutline && size === "base" && "bg-sheet",
        !isOutline && size === "sm" && "bg-sheet",
        isOutline && "bg-default",
        isOutline && isHorizontal && "h-0.5",
        isOutline && !isHorizontal && "w-0.5",
        className,
      )}
      style={getIndicatorStyle()}
    />
  );
};

const tabsListVariants = cva("flex items-center", {
  variants: {
    size: {
      base: "p-1 rounded-[10px]",
      sm: "p-0",
    },
    variant: {
      default: "",
      outline: "bg-transparent p-0",
    },
  },
  compoundVariants: [
    { variant: "default", size: "base", className: "bg-surface" },
    { variant: "default", size: "sm", className: "bg-transparent" },
  ],
  defaultVariants: {
    size: "base",
    variant: "default",
  },
});

interface TabsListProps<ClassName extends string = string>
  extends Omit<TabsPrimitive.List.Props, "className">,
    VariantProps<typeof tabsListVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const TabsList = <ClassName extends string>({
  className,
  UNSAFE_className,
  size = "base",
  variant = "default",
  ...props
}: TabsListProps<ClassName>) => {
  const resolvedSize: "base" | "sm" = size ?? "base";
  const resolvedVariant: "default" | "outline" = variant ?? "default";

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={resolvedVariant}
      className={cn(
        "group/tabs-list relative",
        tabsListVariants({ size, variant }),
        "aria-[orientation=vertical]:flex-col aria-[orientation=vertical]:[&>*]:w-full aria-[orientation=vertical]:[&>*]:justify-start",
        "aria-[orientation=horizontal]:flex-row",
        resolvedSize === "base" && "aria-[orientation=vertical]:gap-2",
        resolvedSize === "sm" && "aria-[orientation=vertical]:gap-1",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      <TabsPrimitive.Indicator
        render={(indicatorProps, state): React.ReactElement<unknown> => {
          const { activeTabPosition, activeTabSize, orientation } = state;

          return (
            <AnimatedIndicator
              activeTabPosition={activeTabPosition}
              activeTabSize={activeTabSize}
              orientation={orientation}
              variant={resolvedVariant}
              size={resolvedSize}
              className={indicatorProps.className}
            />
          );
        }}
      />
      {props.children}
    </TabsPrimitive.List>
  );
};

const tabsTriggerVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 font-medium leading-none z-10",
    "rounded-lg px-3 py-1.5 h-9 border-0.5 border-transparent",
    "text-muted opacity-70 [transition:color_250ms_ease-out,opacity_250ms_ease-out] outline-none cursor-pointer select-none whitespace-nowrap",
    "focus-visible:border-border focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "hover:opacity-100 hover:text-default",
    "data-active:text-default data-active:opacity-100",
    "data-disabled:opacity-30 data-disabled:cursor-not-allowed data-disabled:pointer-events-auto",
    "data-disabled:hover:opacity-30 data-disabled:hover:text-muted data-disabled:hover:scale-[0.98]",
    "group-data-[variant=outline]/tabs-list:border-0 group-data-[variant=outline]/tabs-list:px-1.5 group-data-[variant=outline]/tabs-list:pt-0.5 group-data-[variant=outline]/tabs-list:pb-0",
    "group-data-[variant=outline]/tabs-list:opacity-100 group-data-[variant=outline]/tabs-list:text-muted",
    "group-data-[variant=outline]/tabs-list:hover:text-default",
    "group-data-[variant=outline]/tabs-list:data-active:bg-transparent group-data-[variant=outline]/tabs-list:data-active:border-transparent",
    "group-data-[variant=outline]/tabs-list:data-active:shadow-none group-data-[variant=outline]/tabs-list:data-active:text-default",
    "group-data-[variant=outline]/tabs-list:group-aria-[orientation=vertical]/tabs-list:pl-3",
    "group-data-[variant=outline]/tabs-list:group-aria-[orientation=vertical]/tabs-list:rounded-l-none",
  ],
  {
    variants: {
      size: {
        base: [
          "text-base flex-1",
          "[&_svg:not([class*='size-'])]:size-4",
          "data-active:bg-transparent",
        ],
        sm: [
          "text-sm shrink-0",
          "[&_svg:not([class*='size-'])]:size-3.5",
          "data-active:bg-transparent",
        ],
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

interface TabsTriggerProps<ClassName extends string = string>
  extends Omit<TabsPrimitive.Tab.Props, "className">,
    VariantProps<typeof tabsTriggerVariants> {
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const TabsTrigger = <ClassName extends string>({
  className,
  UNSAFE_className,
  size = "base",
  startEnhancer,
  endEnhancer,
  children,
  ...props
}: TabsTriggerProps<ClassName>) => {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ size }), className, UNSAFE_className)}
      {...props}
    >
      {startEnhancer}
      {children}
      {endEnhancer}
    </TabsPrimitive.Tab>
  );
};

interface TabsContentProps<ClassName extends string = string>
  extends Omit<TabsPrimitive.Panel.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const TabsContent = <ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TabsContentProps<ClassName>) => {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("focus-visible:outline-none", className, UNSAFE_className)}
      {...props}
    />
  );
};

export { Tabs, TabsList, TabsTrigger, TabsContent };

export type { TabsProps, TabsListProps, TabsTriggerProps, TabsContentProps };
